<template>
  <div class="app-root">
    <div
      v-if="showMaintenanceUnavailable"
      class="construction-screen"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="maintenance-unavailable-title"
    >
      <div class="construction-screen-content">
        <img
          src="@/assets/images/logo.png"
          alt="Heaven's Fruit"
          class="construction-logo"
          width="240"
          height="240"
          decoding="async"
        >
        <p class="construction-kicker">Heaven's Fruit</p>
        <h1 id="maintenance-unavailable-title">No pudimos verificar el estado del sitio</h1>
        <p class="construction-copy">El servicio no está respondiendo. Intentá de nuevo en unos instantes.</p>
        <div class="maintenance-form">
          <button
            class="maintenance-submit"
            type="button"
            :disabled="isLoadingMaintenanceSettings"
            @click="loadMaintenanceSettings"
          >
            {{ isLoadingMaintenanceSettings ? 'Reintentando...' : 'Reintentar' }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="showMaintenance"
      class="construction-screen"
      role="dialog"
      aria-modal="true"
      aria-labelledby="construction-title"
    >
      <div class="construction-screen-content">
        <img
          src="@/assets/images/logo.png"
          alt="Heaven's Fruit"
          class="construction-logo"
          width="240"
          height="240"
          decoding="async"
        >
        <p class="construction-kicker">Heaven's Fruit</p>
        <h1 id="construction-title">Todavía estamos construyendo la web</h1>
        <p class="construction-copy">Volvemos pronto.</p>
        <form class="maintenance-form" @submit.prevent="unlockMaintenance">
          <label class="visually-hidden" for="maintenance-password">
            Contraseña de acceso
          </label>
          <input
            id="maintenance-password"
            v-model="maintenancePassword"
            class="maintenance-password"
            type="password"
            placeholder="Contraseña de acceso"
            autocomplete="current-password"
            required
          >
          <button class="maintenance-submit" type="submit">
            Desbloquear
          </button>
          <p v-if="maintenanceError" class="maintenance-error" role="alert">
            {{ maintenanceError }}
          </p>
        </form>
        <router-link to="/login" class="maintenance-login">
          Iniciar sesión
        </router-link>
      </div>
    </div>

    <!-- Sin v-if: evita remount que some herramientas cuentan como CLS al retirar el overlay -->
    <div aria-hidden="true">
      <div class="app-loader" :class="{ 'app-loader--hidden': !isLoading }" :aria-busy="isLoading">
      <div class="app-loader-content">
        <div class="logo-container">
          <img src="@/assets/images/logo.png" alt="Heaven's Fruit Logo" class="loader-logo" width="120" height="120" decoding="async">
        </div>
        <div class="spinner-container">
          <div class="spinner-circle"></div>
        </div>
      </div>
    </div>
      <AgeVerification />
      <PageCurtain />
      <!-- El contenido entra dentro de un recuadro que sube hasta el tope -->
      <div
        class="page-reveal"
        :class="{ 'page-reveal--in': revealPage }"
      >
        <NavbarComponent v-if="!isAccountSettingsRoute" />
        <div :class="{'panel-route': isAccountSettingsRoute, 'main-content': !isAccountSettingsRoute}">
          <router-view />
        </div>
        <FooterComponent v-if="!isAccountSettingsRoute" />
      </div>
    </div>
  </div>
</template>
<script setup>
import AgeVerification from '@/components/AgeVerification.vue'
import PageCurtain from '@/components/common/PageCurtain.vue'
import NavbarComponent from '@/components/NavbarComponent.vue'
import FooterComponent from '@/components/FooterComponent.vue'
import '@/assets/css/variables.css'
import { curtainOrigin, pageCurtainPhase } from '@/composables/usePageTransition'
import { onMounted, computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getSiteSettings, unlockMaintenance as validateMaintenancePassword } from '@/services/settings';

const route = useRoute();
const router = useRouter();
const maintenancePassword = ref('');
const maintenanceError = ref('');
const maintenanceEnabled = ref(true);
const maintenanceSettingsLoaded = ref(false);
const maintenanceSettingsUnavailable = ref(false);
const isLoadingMaintenanceSettings = ref(false);
const isUnlockingMaintenance = ref(false);
const maintenanceUnlocked = ref(
  sessionStorage.getItem('maintenanceUnlocked') === 'true'
);
/* Loader inicial (spinner sobre negro) visible desde el primer render; se va
   con opacidad apenas monta la app. */
const isLoading = ref(true);
const isAccountSettingsRoute = computed(() => 
  route.meta.panel === true
);
/* El revelado del recuadro sólo aplica a los cambios de página, no al menú */
const revealPage = computed(() =>
  pageCurtainPhase.value === 'revealing' && curtainOrigin.value === 'page'
);
const isAuthenticated = computed(() => !!localStorage.getItem('accessToken'));
const showMaintenance = computed(() =>
  maintenanceSettingsLoaded.value &&
  !maintenanceSettingsUnavailable.value &&
  !isAuthenticated.value &&
  maintenanceEnabled.value &&
  !maintenanceUnlocked.value &&
  !route.meta.redirectIfAuth
);
const showMaintenanceUnavailable = computed(() =>
  maintenanceSettingsLoaded.value &&
  maintenanceSettingsUnavailable.value &&
  !isAuthenticated.value &&
  !route.meta.redirectIfAuth
);

const loadMaintenanceSettings = async () => {
  if (isLoadingMaintenanceSettings.value) return;

  isLoadingMaintenanceSettings.value = true;
  try {
    const settings = await getSiteSettings();
    maintenanceEnabled.value = settings.maintenance_enabled !== false;
    if (!maintenanceEnabled.value) {
      maintenanceUnlocked.value = true;
    }
    maintenanceSettingsUnavailable.value = false;
  } catch (error) {
    maintenanceSettingsUnavailable.value = true;
    console.error('Error al cargar ajustes de mantenimiento:', error);
  } finally {
    maintenanceSettingsLoaded.value = true;
    isLoadingMaintenanceSettings.value = false;
  }
};

const unlockMaintenance = async () => {
  if (isUnlockingMaintenance.value) return;

  isUnlockingMaintenance.value = true;
  try {
    await validateMaintenancePassword(maintenancePassword.value);
    maintenanceError.value = '';
    maintenancePassword.value = '';
    maintenanceUnlocked.value = true;
    sessionStorage.setItem('maintenanceUnlocked', 'true');
    router.push('/');
  } catch (error) {
    maintenanceError.value = error.response?.data?.detail
      || 'Contraseña incorrecta.';
    maintenancePassword.value = '';
  } finally {
    isUnlockingMaintenance.value = false;
  }
};

onMounted(() => {
  // El spinner se va con opacidad apenas la app termina de montar. El rAF
  // asegura un primer paint con el spinner visible; el timeout le da un
  // mínimo en pantalla para que no sea un flash.
  requestAnimationFrame(() => {
    setTimeout(() => {
      isLoading.value = false;
    }, 500);
  });

  loadMaintenanceSettings();

  // Verificar si hay datos en caché del formato antiguo y limpiarlos
  const oldCacheKeys = [
    'products',
    'productsTimestamp',
    'userOrders',
    'adminOrders',
    'ordersTimestamp'
  ];

  const hasOldCache = oldCacheKeys.some(key => {
    const value = localStorage.getItem(key);
    try {
      if (value) JSON.parse(value);
      return true;
    } catch {
      return false;
    }
  });

  if (hasOldCache && !localStorage.getItem('cacheVersion')) {
    console.log('Detectado caché antiguo, limpiando...');
    oldCacheKeys.forEach(key => localStorage.removeItem(key));
  }

});
</script>

<style>
@import './assets/css/variables.css';
@import './assets/css/components.css';

/* ------------------------------------------------------------------
   Helvetica real en TODAS las plataformas (10/10)
   Windows no trae Helvetica: el sistema la sustituye por Arial (clave
   FontSubstitutes del registro: "Helvetica" = "Arial"), asi que en PC el
   navegador renderizaba Arial y la "R" salia mas recta que en el iPhone
   (Helvetica de verdad). Estos woff2 son Helvetica (clon metrico TeX Gyre
   Heros / Nimbus Sans, licencia GUST Font License) auto-hospedados y van
   PRIMERO en la lista: el navegador toma el webfont antes de caer en la
   sustitucion de Windows, y en iPhone/macOS usa las mismas formas.
   Metricas identicas a Helvetica/Arial => no cambia ningun layout.
   Pesos: el 400-500 cae en Regular y el 600-900 en Bold, igual que antes
   (ni Helvetica ni Arial tienen Medium en Windows/iOS).
   ------------------------------------------------------------------ */
@font-face {
  font-family: 'Helvetica Clone';
  src: url('./assets/fonts/helvetica-regular.woff2') format('woff2');
  font-weight: 100 500;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Helvetica Clone';
  src: url('./assets/fonts/helvetica-bold.woff2') format('woff2');
  font-weight: 600 900;
  font-style: normal;
  font-display: swap;
}
@font-face {
  font-family: 'Helvetica Clone';
  src: url('./assets/fonts/helvetica-italic.woff2') format('woff2');
  font-weight: 100 500;
  font-style: italic;
  font-display: swap;
}

.app-root {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.construction-screen {
  position: fixed;
  inset: 0;
  z-index: 20000;
  display: grid;
  place-items: center;
  padding: 2rem;
  overflow: hidden;
  background: #000000;
  color: #ffffff;
  text-align: center;
}

.construction-screen::before,
.construction-screen::after {
  content: '';
  position: absolute;
  left: 50%;
  width: min(72vw, 680px);
  height: 1px;
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(-50%);
}

.construction-screen::before {
  top: 2rem;
}

.construction-screen::after {
  bottom: 2rem;
}

.construction-screen-content {
  position: relative;
  z-index: 1;
  display: grid;
  justify-items: center;
  width: min(100%, 680px);
}

.construction-logo {
  display: block;
  width: clamp(170px, 31vw, 290px);
  height: auto;
  margin-bottom: clamp(2rem, 5vh, 3.5rem);
  opacity: 0.96;
}

.construction-kicker {
  margin: 0 0 1rem;
  color: rgba(255, 255, 255, 0.52);
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.3em;
  text-transform: uppercase;
}

.construction-screen h1 {
  max-width: 15ch;
  margin: 0;
  font-size: clamp(1.7rem, 4vw, 3.5rem);
  font-weight: 500;
  letter-spacing: 0;
  line-height: 0.98;
}

.construction-copy {
  margin: 1.5rem 0 0;
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.78rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}

.maintenance-form {
  display: grid;
  gap: 0.75rem;
  width: min(100%, 320px);
  margin-top: 2rem;
}

.maintenance-password,
.maintenance-submit {
  min-height: 2.75rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 0;
  padding: 0.75rem 1rem;
  font: inherit;
}

.maintenance-password {
  background: rgba(255, 255, 255, 0.08);
  color: #ffffff;
  outline: none;
  text-align: center;
}

.maintenance-password::placeholder {
  color: rgba(255, 255, 255, 0.55);
}

.maintenance-password:focus {
  border-color: rgba(255, 255, 255, 0.8);
}

.maintenance-submit {
  background: #ffffff;
  color: #000000;
  cursor: pointer;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.maintenance-submit:hover {
  background: rgba(255, 255, 255, 0.8);
}

.maintenance-submit:disabled {
  cursor: wait;
  opacity: 0.7;
}

.maintenance-error {
  margin: 0;
  color: #ffb4b4;
  font-size: 0.8rem;
}

.maintenance-login {
  margin-top: 1.1rem;
  display: inline-block;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.78rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  text-decoration: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
  transition: color 0.2s ease, border-color 0.2s ease;
}

.maintenance-login:hover {
  color: #ffffff;
  border-color: #ffffff;
}

@media (max-width: 600px) {
  .construction-screen {
    padding: 1.5rem;
  }

  .construction-screen::before {
    top: 1.5rem;
  }

  .construction-screen::after {
    bottom: 1.5rem;
  }
}

.main-content,
.panel-route {
  flex: 1 0 auto;
}

.panel-route,
.panel-route *:not(.fa):not(.fas):not(.far):not(.fab):not(.fa-solid):not(.fa-regular):not(.fa-brands) {
  font-family: 'Helvetica Clone', Helvetica, 'Helvetica Neue', Arial, sans-serif !important;
}

* {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: 'Helvetica Clone', Helvetica, 'Helvetica Neue', Arial, sans-serif;
    scroll-behavior: smooth;
}

/* También los pseudo-elementos: son los que suelen quedar con la fuente del navegador */
*::before,
*::after {
    font-family: 'Helvetica Clone', Helvetica, 'Helvetica Neue', Arial, sans-serif;
}

/* Los glifos de Font Awesome viven en pseudo-elementos: preservamos su fuente. */
.fa::before, .fas::before, .far::before, .fab::before,
.fa-solid::before, .fa-regular::before, .fa-brands::before {
  font-family: var(--_fa-family, "Font Awesome 7 Free") !important;
  font-style: normal;
}

/* Legacy variable mappings to maintain compatibility with existing code */
:root {
    /* Base Colors - mapped to new variables */
    --black-color: #000;
    --black-color-2: var(--black-coor-2);
    --black-color-3: var(--black-soft);
    --grey-color: var(--gray-400);
    --bg-color: var(--bg-main);
    --white-color: var(--text-primary);
    --white-color-2: var(--text-secondary);

    /* Primary Color - monocromo (blanco y negro), definido en variables.css.
       Antes había una auto-referencia (--primary-color: var(--primary-color)) que
       anulaba la variable para todo el sitio. */
    --primary-color-subtle: rgba(255, 255, 255, 0.2);
    --primary-color-rgb: 255, 255, 255;
    --dark-color: var(--black-rich);

    /* Secondary Colors - remapped */
    --light-primary-color: var(--primary-green-light);
    --dark-primary-color: var(--primary-green-dark);
    --dark-primary-color-2: var(--black-charcoal);
    --secondary-color: var(--secondary-gold);
    --accent-color: var(--secondary-gold-light);

    /* Utility Colors */
    --primary: rgba(255, 255, 255, 0.15);
    --green-color: var(--success);
    --tertiary-color: var(--secondary-gold-dark);
}
/* Bootstrap en monocromo (componentes que no leen nuestras variables) */
:root {
    --bs-body-font-family: 'Helvetica Clone', Helvetica, 'Helvetica Neue', Arial, sans-serif;
    --bs-primary: #ffffff;
    --bs-primary-rgb: 255, 255, 255;
    --bs-link-color: #ffffff;
    --bs-link-color-rgb: 255, 255, 255;
    --bs-link-hover-color: var(--primary-color-dark);
    --bs-link-hover-color-rgb: 189, 189, 189;
}

/* Checks y radios: relleno blanco con tilde/punto negro */
.form-check-input:checked {
    background-color: var(--primary-green);
    border-color: var(--primary-green);
    color: #000000;
}
.form-check-input:checked[type="checkbox"] {
    --bs-form-check-bg-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 20 20'%3e%3cpath fill='none' stroke='%23000' stroke-linecap='round' stroke-linejoin='round' stroke-width='3' d='m6 10 3 3 6-6'/%3e%3c/svg%3e");
}
.form-check-input:checked[type="radio"] {
    --bs-form-check-bg-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='2' fill='%23000'/%3e%3c/svg%3e");
}
.form-check-input:focus {
    border-color: var(--primary-green);
    box-shadow: 0 0 0 0.25rem rgba(255, 255, 255, 0.25);
}

body {
    background-color: #000000;
}

body.catalog-filters-open { overflow: hidden; }

/* Bootstrap define su propio stack en body; se unifica con el resto del sitio. */
html,
body {
    font-family: 'Helvetica Clone', Helvetica, 'Helvetica Neue', Arial, sans-serif;
}
.container {
    padding: 0;
    max-width: var(--container-width, 1200px);
}
.bg-dark {
    background-color: var(--black-color-3) !important;
}
.border-primary {
    border-color: var(--primary-green) !important;
}	
/* Efecto de texto con contorno */
.text-outline {
  color: transparent;
  text-shadow: 
    -1px -1px 0 rgba(255,255,255,0.4),
    1px -1px 0 rgba(255,255,255,0.4),
    -1px 1px 0 rgba(255,255,255,0.4),
    1px 1px 0 rgba(255,255,255,0.4);
  -webkit-text-stroke: 1px rgba(255,255,255,0.6);
  background: linear-gradient(
    90deg,
    rgba(255,255,255,0.2),
    rgba(255,255,255,0.6)
  );
  -webkit-background-clip: text;
  background-clip: text;
  will-change: opacity;
  animation: textOutlinePulse 5s ease-in-out infinite;
}

.text-muted{
  color: #acacac !important;
}

@keyframes textOutlinePulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.88; }
}
.display-2, .display-3, .display-4, .display-5, .text-white {
    font-weight: 800;
    color: var(--text-secondary);
}

section{
min-height: 50vh;
position: relative;
overflow: hidden;
}

.h5{
font-size: 1.1rem;
font-weight: 500;
color: var(--white-color);
}
.fs-6{
font-size: .8rem;
}
.fs-7{
font-size: .78rem;
}
.btn{
font-size: .95rem;
}

.hover-opacity{
transition: 0.3s ease;
}
.hover-opacity:hover{
opacity: 0.8;
}

/* badges */
.status-badge {
display: inline-flex;
padding: 0.4rem 0.8rem;
border-radius: 2rem;
font-size: 0.875rem;
font-weight: 500;
white-space: nowrap;
}
.btn-outline-primary {
    background-color: transparent;
    color: var(--primary-green);
    border: 1px solid var(--primary-green);
    cursor: pointer;
    transition: 0.3s ease;
}
.display-5{
  font-family: 'Helvetica Clone', Helvetica, 'Helvetica Neue', Arial, sans-serif;
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 800 !important;
}
.btn-outline-primary.active {
    background-color: var(--primary-green);
    color: #000000;
    border: 1px solid var(--primary-green);
}
.btn-outline-primary:hover {
    background-color: var(--primary-green);
    color: #000000;
    border: 1px solid var(--primary-green);
}
.btn-outline-primary:hover i {
    color: #000000;
}
.text-primary {
    color: var(--primary-green) !important;
}
.bg-primary {
    background-color: var(--primary-green) !important;
  color: #000000;
}
.text-dark{
color: var(--black-color);
}
.btn{

padding: 0.5rem 1rem;
}
.btn-primary {
    background: var(--primary-color);
    color: #000000;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    transition: 0.3s ease;
}
.btn{
  font-weight: 700;
}
.btn-primary:hover {
    background: var(--primary-color-dark);
    color: #000000;
    opacity: 1;
    box-shadow: var(--shadow-accent);
}
button,a{
font-weight: 600;
font-size: 1.1rem;
}
h1, h2, h3, h4, h5, h6 {
font-family: 'Helvetica Clone', Helvetica, 'Helvetica Neue', Arial, sans-serif;
font-weight: 600;
letter-spacing: -.02em;
line-height: 1.14;
color: var(--white-color);
}
h1 {
font-size: clamp(1.7rem, 2.4vw, 2rem);
}
h2 {
font-size: clamp(1.45rem, 2vw, 1.7rem);
}
h3 {
font-size: clamp(1.15rem, 1.7vw, 1.45rem);
font-weight: 600;
letter-spacing: -.015em;
}
h4 {
font-size: clamp(1rem, 1.4vw, 1.2rem);
font-weight: 600;
letter-spacing: -.01em;
}
h5 {
font-size: .8rem;
font-weight: 600;
letter-spacing: .01em;
}
p{
font-size: 1rem;
color: var(--white-color-2);
font-weight: 300;
}
b{
font-weight: 700;
}

@media (max-width: 525px) {
  .display-2{
    font-size: 3.5rem !important;
  }
}
@media (max-width: 400px) {
  .display-2{
    font-size: 3rem !important;
  }
}
/* =========================================================
   REVELADO DE PÁGINA
   El contenido entra dentro de un recuadro que sube hasta el tope; lo que
   queda afuera del recuadro se ve negro (la cortina / el fondo del sitio).
   ========================================================= */
.page-reveal--in {
  animation: page-reveal-up var(--curtain-salida, 620ms) cubic-bezier(0.33, 1, 0.68, 1) both;
}

@keyframes page-reveal-up {
  from {
    clip-path: inset(100% 0 0 0);
  }

  to {
    clip-path: inset(0 0 0 0);
  }
}


@media (prefers-reduced-motion: reduce) {
  .page-reveal--in {
    animation: none;
  }
}


/* Loader principal */
  .app-loader {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--black-color);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10001;
-webkit-backface-visibility: hidden;
    backface-visibility: hidden;
    transition: opacity 0.45s ease, visibility 0s linear 0.45s;
  }

  .app-loader--hidden {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
  
  .app-loader-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  
  .logo-container {
    position: relative;
    width: 180px;
    height: 180px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .loader-logo {
    width: 150px;
    height: auto;
    position: relative;
    z-index: 1;
    animation: pulse 2s infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes pulse {
    0% { transform: scale(1); opacity: 0.8; }
    50% { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1); opacity: 0.8; }
  }
  
  .spinner-container {
    margin-top: 15px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  .spinner-circle {
    width: 25px;
    height: 25px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top: 2px solid var(--primary-green);
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  /* Estilos para las estrellas en todas las secciones */
  .stars-container {
    pointer-events: none;
    overflow: hidden;
    z-index: 0;
    container-type: size;
  }

  .star {
    position: absolute;
    left: 0;
    top: 0;
    transform: translate3d(calc(var(--sx, 0) * 1cqw), calc(var(--sy, 0) * 1cqh), 0);
    background-color: #ffffff;
    border-radius: 50%;
    animation: starTwinkle var(--duration, 3s) infinite ease-in-out alternate;
    opacity: 0;
    z-index: 0;
    will-change: opacity, transform;
  }

  .section-stars-manager {
    display: none;
  }

  @keyframes starTwinkle {
    0% {
      opacity: 0;
    }
    50% {
      opacity: 0.8;
    }
    100% {
      opacity: 0.3;
    }
  }
</style>
